import * as React from "react"
import { cn } from "@/lib/utils"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Skeleton } from "@/components/ui/skeleton"

/**
 * Responsive data table.
 *
 * Desktop (≥ md): renders a real <table> with sticky header.
 * Mobile  (< md): stacks each row as a card — the column header becomes
 *                 a label next to the cell's value.
 *
 * Give each column a stable ``key`` and a ``header``. Use ``renderCell`` to
 * return the ReactNode for that cell. Optional ``primary`` promotes the
 * cell to the top of the mobile card (no label, larger text). Optional
 * ``align`` right-aligns numeric columns on desktop.
 */

export interface DataTableColumn<T> {
  key: string
  header: React.ReactNode
  /** Returns the ReactNode rendered in the cell. */
  renderCell: (row: T, index: number) => React.ReactNode
  /** On mobile, promote this cell to the card's prominent header row. */
  primary?: boolean
  /** On mobile, show this column as a right-side badge next to `primary`. */
  badge?: boolean
  /** Desktop alignment. */
  align?: "left" | "right" | "center"
  /** Optional class applied to header + cells (both desktop & mobile). */
  className?: string
  /** Hide on mobile entirely. */
  hiddenOnMobile?: boolean
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  rows: T[]
  rowKey: (row: T, index: number) => React.Key
  loading?: boolean
  skeletonRows?: number
  empty?: React.ReactNode
  onRowClick?: (row: T) => void
  className?: string
  /** Sticky-header wrapper max-height (CSS value). */
  maxHeight?: string
}

function alignToClass(a: DataTableColumn<any>["align"]) {
  if (a === "right") return "text-right"
  if (a === "center") return "text-center"
  return "text-left"
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  skeletonRows = 6,
  empty,
  onRowClick,
  className,
  maxHeight,
}: DataTableProps<T>) {
  const visibleMobileColumns = columns.filter((c) => !c.hiddenOnMobile)

  if (loading) {
    return (
      <div className={cn("space-y-2", className)}>
        {/* Desktop skeleton */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((c) => (
                  <TableHead
                    key={c.key}
                    className={cn(alignToClass(c.align), c.className)}
                  >
                    {c.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: skeletonRows }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((c) => (
                    <TableCell key={c.key} className={cn(alignToClass(c.align), c.className)}>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Mobile skeleton */}
        <div className="space-y-2 md:hidden">
          {Array.from({ length: skeletonRows }).map((_, i) => (
            <div key={i} className="rounded-md border bg-card p-3">
              <Skeleton className="mb-2 h-4 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!rows.length) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center rounded-md border bg-card p-10 text-center text-sm text-muted-foreground",
          className,
        )}
      >
        {empty ?? "No results."}
      </div>
    )
  }

  return (
    <div className={cn(className)}>
      {/* Desktop table */}
      <div
        className="hidden overflow-auto rounded-md border bg-card md:block"
        style={maxHeight ? { maxHeight } : undefined}
      >
        <Table>
          <TableHeader className="bg-muted/40">
            <TableRow>
              {columns.map((c) => (
                <TableHead
                  key={c.key}
                  className={cn(
                    "sticky top-0 z-10 bg-muted/40 backdrop-blur supports-[backdrop-filter]:bg-muted/60",
                    alignToClass(c.align),
                    c.className,
                  )}
                >
                  {c.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow
                key={rowKey(row, i)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? "cursor-pointer" : undefined}
              >
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    className={cn(alignToClass(c.align), c.className)}
                  >
                    {c.renderCell(row, i)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <ul className="space-y-2 md:hidden" role="list">
        {rows.map((row, i) => {
          const primary = visibleMobileColumns.find((c) => c.primary)
          const badge = visibleMobileColumns.find((c) => c.badge)
          const rest = visibleMobileColumns.filter((c) => c !== primary && c !== badge)
          return (
            <li
              key={rowKey(row, i)}
              className={cn(
                "rounded-md border bg-card p-3 shadow-sm",
                onRowClick && "cursor-pointer active:bg-muted/60",
              )}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {(primary || badge) && (
                <div className="mb-2 flex items-start justify-between gap-3">
                  <div className="min-w-0 text-sm font-medium leading-tight">
                    {primary ? primary.renderCell(row, i) : null}
                  </div>
                  {badge ? (
                    <div className="shrink-0 text-sm">{badge.renderCell(row, i)}</div>
                  ) : null}
                </div>
              )}
              {rest.length > 0 && (
                <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
                  {rest.map((c) => (
                    <React.Fragment key={c.key}>
                      <dt className="text-muted-foreground">{c.header}</dt>
                      <dd className="min-w-0 break-words text-right text-foreground">
                        {c.renderCell(row, i)}
                      </dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
